import type { DocMigration } from "../docMigration";

function deleteFromIncludedBy(entityRef: any, key: string): void {
  if (!entityRef.includedBy) {
    return;
  }
  // Copy before modifying so that we don't mutate the backup.
  entityRef.includedBy = { ...entityRef.includedBy };
  if (entityRef.includedBy[key]) {
    delete entityRef.includedBy[key];
  }
}

const annotationPrefix = "annotation.";
const annotationPrefixLength = annotationPrefix.length;

function isFormula(v: any): boolean {
  return !!v && typeof v === "object" && v.hasOwnProperty("type") && v.type === "FORMULA";
}

function formulaNew(formula: string): { type: "FORMULA"; formula: string } {
  return { type: "FORMULA", formula };
}

function mergeDisabled(disabled1?: boolean | any, disabled2?: boolean | any): any {
  if (
    !isFormula(disabled1) ||
    (isFormula(disabled1) && (disabled1.formula === "false" || disabled1.formula === "not(true)"))
  ) {
    // We don't expect disabled to be set to boolean true, so we just return the other disabled state in this case.
    return disabled2;
  }

  if (
    !isFormula(disabled2) ||
    (isFormula(disabled2) && (disabled2.formula === "false" || disabled2.formula === "not(true)"))
  ) {
    return disabled1;
  }

  return formulaNew(`(${disabled1.formula}) or (${disabled2.formula})`);
}

const migration: DocMigration = {
  id: "20260508_annotation_catalog_to_drawing_template",
  description: "Change annotation templates to drawing templates",
  apply(json: any): any {
    const contentTemplateInstances = json.contentTemplateInstances;

    // The old data in the doc is dumped in here so we can recover it if needed.
    json.legacyAnnotationCatalogSettings = {};
    json.legacyAnnotationTemplateInstances = {};
    json.legacyAnnotationCatalogEntityRefs = {};

    const contentTemplates = json.contentTemplates;
    const settingsTable = json.settings ?? {};
    json.settings = settingsTable;
    const settingsKeys = Object.keys(settingsTable);
    const annotationTable = json.annotation ?? {};
    json.annotation = annotationTable;
    const groups = json.groups ?? {};
    json.groups = groups;
    const entityGroups = json.entityGroups ?? {};
    json.entityGroups = entityGroups;
    const entityRefs = json.entityRefs ?? {};
    json.entityRefs = entityRefs;
    const entityRoomLayouts = json.entityRoomLayouts ?? {};
    json.entityRoomLayouts = entityRoomLayouts;
    const templateToSettingsSetDisabled: {
      [templateId: string]: boolean | { type: "FORMULA"; formula: string } | undefined;
    } = {};

    for (const settingsId of settingsKeys) {
      const settings = settingsTable[settingsId];
      if (settings.type !== "ANNOTATION_CATALOG_SETTINGS") {
        continue;
      }
      delete settingsTable[settingsId];
      json.legacyAnnotationCatalogSettings[settingsId] = structuredClone(settings);

      const annotationTemplates = settings.annotationTemplates ?? {};

      if (Object.keys(annotationTemplates).length === 0) {
        continue;
      }

      const settingsSetId = settings.parentId;
      const settingsSetDisabled = json.settingsSets?.[settingsSetId]?.disabled;

      // If the settings set is doc ref'd (present in json.entityRefs table)
      // then the new drawing templates should be doc ref'd as well.
      // See migration 459 for a similar example.
      // Doc needs to be published before doc refing is possible.
      const preservedDocRefFromSettingsSet = entityRefs[settingsSetId] ? { ...entityRefs[settingsSetId] } : undefined;
      if (preservedDocRefFromSettingsSet) {
        json.legacyAnnotationCatalogEntityRefs[settingsSetId] = entityRefs[settingsSetId];
        // Don't delete the original entity ref for the settings set, since it might contain other settings than
        // just the annotation templates. However, clean up the preserved includedBy so that it doesn't contain the deleted settings.
        deleteFromIncludedBy(entityRefs[settingsSetId], "settings." + settingsId);

        if (preservedDocRefFromSettingsSet.includedBy) {
          deleteFromIncludedBy(preservedDocRefFromSettingsSet, "settingsSet." + settingsSetId);
          deleteFromIncludedBy(preservedDocRefFromSettingsSet, "settings." + settingsId);
        }
      }

      const docRefOldSettingsTemplate = entityRefs[settingsId];
      if (docRefOldSettingsTemplate) {
        json.legacyAnnotationCatalogEntityRefs[settingsId] = entityRefs[settingsId];
        delete entityRefs[settingsId];
      }

      const group = {
        entityType: "GROUP",
        id: settings.id,
        name: settings.name,
        isDeletable: true,
        sortIndex: 0,
      };
      groups[group.id] = group;

      // The group is a brand-new entity created deterministically (id === settings.id) in every
      // migrated doc. If the source catalog was doc ref'd, the group must be registered as an entity ref too.
      if (preservedDocRefFromSettingsSet) {
        const newGroupEntityRef: any = {
          ...structuredClone(preservedDocRefFromSettingsSet), // copies "disabled", "docRefId", and doc ref V2 fields if present
          id: group.id,
          entityType: "GROUP",
        };
        if (newGroupEntityRef.entityRefType === "V2") {
          // New entities don't have settings as a parent, so the group is always included by itself.
          newGroupEntityRef.includedBy = {
            ...newGroupEntityRef.includedBy,
            ["group." + group.id]: { type: "GROUP", id: group.id },
          };
        }
        entityRefs[group.id] = newGroupEntityRef;
      }

      for (const annotationTemplateId of Object.keys(annotationTemplates)) {
        const annotationTemplate = annotationTemplates[annotationTemplateId];
        templateToSettingsSetDisabled[annotationTemplateId] = settingsSetDisabled;

        const drawingTemplateLabels = { ...annotationTemplate.labels };

        const drawingTemplate = {
          entityType: "CONTENT_TEMPLATE",
          templateType: "DRAWING",
          drawingType: "ANNOTATION",
          id: annotationTemplateId,
          name: annotationTemplate.name,
          viewScale: 1 / 4 / 12, // 1/4" = 1'
          labels: drawingTemplateLabels,
          sortIndex: annotationTemplate.sortIndex ?? 0,
        };
        contentTemplates[annotationTemplateId] = drawingTemplate;

        if (preservedDocRefFromSettingsSet) {
          const newEntityRef: any = {
            ...structuredClone(preservedDocRefFromSettingsSet), // This will copy "disabled", "docRefId", as well as doc ref V2 fields if present
            id: annotationTemplateId,
            entityType: "CONTENT_TEMPLATE",
          };
          if (newEntityRef.entityRefType === "V2") {
            // New entities don't have settings as a parent, so we'll always add includedBy itself.
            newEntityRef.includedBy = {
              ...newEntityRef.includedBy,
              ["contentTemplate." + annotationTemplateId]: { type: "CONTENT_TEMPLATE", id: annotationTemplateId },
            };
          }
          entityRefs[annotationTemplateId] = newEntityRef;
        }

        // Group the drawing templates according to the settings set they're coming from.
        entityGroups["contentTemplate." + annotationTemplateId] = group.id;

        let originX = annotationTemplate.originX;
        let originY = annotationTemplate.originY;

        if (originX === undefined || originY === undefined) {
          // Origin should be the center of the template bounding box.
          if (annotationTemplate.aabb) {
            originX = annotationTemplate.aabb.minX + (annotationTemplate.aabb.maxX - annotationTemplate.aabb.minX) / 2;
            originY = annotationTemplate.aabb.minY + (annotationTemplate.aabb.maxY - annotationTemplate.aabb.minY) / 2;
          } else {
            originX = 0;
            originY = 0;
          }
        }

        const components = annotationTemplate.components;
        for (const annotationId of Object.keys(components)) {
          const annotation = components[annotationId];
          delete annotation.handle;
          annotationTable[annotationId] = annotation;
          annotation.entityType = "ANNOTATION";
          annotation.id = annotationId;
          annotation.parentId = annotationTemplateId;
          annotation.labels = { ...drawingTemplateLabels };
          annotation.positioningMethod = "GLOBAL";
          annotation.sortIndex = 0;
          if (annotation.type === "NOTE") {
            annotation.x = annotation.x - originX;
            annotation.y = annotation.y - originY;
            annotation.leaderLineX =
              annotation.leaderLineX !== undefined ? annotation.leaderLineX - originX : undefined;
            annotation.leaderLineY =
              annotation.leaderLineY !== undefined ? annotation.leaderLineY - originY : undefined;
            annotation.landingLineX =
              annotation.landingLineX !== undefined ? annotation.landingLineX - originX : undefined;
            annotation.landingLineY =
              annotation.landingLineY !== undefined ? annotation.landingLineY - originY : undefined;
          } else if (annotation.type === "LINEWORK") {
            if (annotation.path) {
              const mapped = new Float64Array(annotation.path.length);
              for (let i = 0; i < annotation.path.length; i += 2) {
                mapped[i] = annotation.path[i] - originX;
                mapped[i + 1] = annotation.path[i + 1] - originY;
              }
              annotation.path = mapped;
            }
          } else if (annotation.type === "LINEAR_DIMENSION") {
            annotation.lineStartX = annotation.lineStartX - originX;
            annotation.lineStartY = annotation.lineStartY - originY;
            annotation.lineEndX = annotation.lineEndX - originX;
            annotation.lineEndY = annotation.lineEndY - originY;
            annotation.startX = annotation.startX - originX;
            annotation.startY = annotation.startY - originY;
            annotation.endX = annotation.endX - originX;
            annotation.endY = annotation.endY - originY;
          }

          if (preservedDocRefFromSettingsSet) {
            const newEntityRef: any = {
              ...structuredClone(preservedDocRefFromSettingsSet), // This will copy "disabled", "docRefId", as well as doc ref V2 fields if present
              id: annotationId,
              entityType: "ANNOTATION",
            };
            if (newEntityRef.entityRefType === "V2") {
              newEntityRef.includedBy = {
                ...newEntityRef.includedBy,
                ["contentTemplate." + annotationTemplateId]: { type: "CONTENT_TEMPLATE", id: annotationTemplateId },
                ["annotation." + annotationId]: { type: "ANNOTATION", id: annotationId },
              };
            }
            entityRefs[annotationId] = newEntityRef;
          }
        }
      }
    }

    const annotationKeys = Object.keys(annotationTable);
    for (const annotationId of annotationKeys) {
      const annotation = annotationTable[annotationId];
      if (annotation.type !== "TEMPLATE_INSTANCE") {
        continue;
      }
      json.legacyAnnotationTemplateInstances[annotationId] = structuredClone(annotation);
      delete annotationTable[annotationId];

      const annotationHash = "annotation." + annotationId;

      if (annotation.annotationTemplateId === "" || contentTemplates[annotation.annotationTemplateId] === undefined) {
        // Handle corrupt annotation template instances (missing id or missing template)

        // Remove dead references
        if (entityGroups[annotationHash]) {
          delete entityGroups[annotationHash];
        }
        if (entityRoomLayouts[annotationHash]) {
          delete entityRoomLayouts[annotationHash];
        }
        if (entityRefs[annotationId]) {
          delete entityRefs[annotationId];
        }

        continue;
      }
      const drawingTemplateInstance = {
        id: annotationId,
        name: annotation.name,
        entityType: "CONTENT_TEMPLATE_INSTANCE",
        templateType: "DRAWING",
        contentTemplateId: annotation.annotationTemplateId,
        parameterValues: {},
        parentId: annotation.parentId,
        positioningMethod: annotation.positioningMethod,
        edgeId: annotation.edgeId,
        x: annotation.x,
        y: annotation.y,
        rotation: annotation.rotation ?? 0,
        sortIndex: annotation.sortIndex ?? 0,
        disabled: mergeDisabled(templateToSettingsSetDisabled[annotation.annotationTemplateId], annotation.disabled),

        // Overrides set here will override all the labels of the template annotations.
        labels: { ...annotation.labels },
      };
      contentTemplateInstances[drawingTemplateInstance.id] = drawingTemplateInstance;

      const newInstanceHash = "contentTemplateInstance." + drawingTemplateInstance.id;

      // Annotation instances that are part of room layouts can be doc ref'd.
      if (entityRefs[annotationId]) {
        const newEntityRef: any = {
          ...entityRefs[annotationId], // This will copy "disabled", "docRefId", as well as doc ref V2 fields if present
          id: drawingTemplateInstance.id,
          entityType: "CONTENT_TEMPLATE_INSTANCE",
        };
        if (newEntityRef.includedBy?.[annotationHash]) {
          // If the old entity ref was V2 and included by itself, we need to clean up the includedBy field.
          newEntityRef.includedBy = {
            ...newEntityRef.includedBy,
            [newInstanceHash]: {
              type: "CONTENT_TEMPLATE_INSTANCE",
              id: drawingTemplateInstance.id,
            },
          };
          delete newEntityRef.includedBy[annotationHash];
        }
        delete entityRefs[annotationId];
        entityRefs[drawingTemplateInstance.id] = newEntityRef;
      }

      if (entityGroups[annotationHash]) {
        entityGroups[newInstanceHash] = entityGroups[annotationHash];
        delete entityGroups[annotationHash];
      }

      if (entityRoomLayouts[annotationHash]) {
        entityRoomLayouts[newInstanceHash] = entityRoomLayouts[annotationHash];
        delete entityRoomLayouts[annotationHash];
      }
    }

    // Clear all remaining possible references to annotation template instances from entity refs.
    const isAnnotationTemplateInstanceKey = (key: string): boolean => {
      return (
        key.startsWith(annotationPrefix) &&
        json.legacyAnnotationTemplateInstances[key.substring(annotationPrefixLength)] !== undefined
      );
    };
    for (const entityRefId of Object.keys(entityRefs)) {
      const entityRef = entityRefs[entityRefId];
      if (entityRef.entityRefType === "V2") {
        const oldKeys = Object.keys(entityRef.includedBy);
        for (const key of oldKeys) {
          if (isAnnotationTemplateInstanceKey(key)) {
            delete entityRef.includedBy[key];
          }
        }
      }
    }

    return json;
  },
};

export default migration;
